import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  FileSpreadsheet, Download, Filter, Search, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RES_STYLES = {
  aprobado: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  reprobado: 'bg-red-100 text-red-700 border-red-300',
  presente: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  ausente: 'bg-red-50 text-red-700 border-red-300',
  pendiente: 'bg-slate-100 text-slate-600 border-slate-300',
};

export default function CapacitadorReporte() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [caps, setCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    capacitacion_id: '',
    desde: '',
    hasta: '',
    resultado: '',
    search: '',
  });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/capacitaciones`, { headers });
        setCaps((r.data || []).filter(c => c.calendarizable));
      } catch { /* ignore */ }
    })();
  }, [headers]);

  const cargar = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filtros.capacitacion_id) q.set('capacitacion_id', filtros.capacitacion_id);
      if (filtros.desde) q.set('desde', filtros.desde);
      if (filtros.hasta) q.set('hasta', filtros.hasta);
      if (filtros.resultado) q.set('resultado', filtros.resultado);
      const r = await axios.get(`${API}/capacitador/reporte-capacitaciones?${q}`, { headers });
      setRows(r.data || []);
    } catch { toast.error('Error al cargar reporte'); }
    setLoading(false);
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filtros.capacitacion_id, filtros.desde, filtros.hasta, filtros.resultado]);

  const filtered = useMemo(() => {
    const q = filtros.search.trim().toLowerCase();
    const base = !q ? rows : rows.filter(r =>
      r.rut_trabajador.toLowerCase().includes(q) ||
      r.nombre_trabajador.toLowerCase().includes(q) ||
      (r.empresa_nombre || '').toLowerCase().includes(q) ||
      r.capacitacion_nombre.toLowerCase().includes(q)
    );
    // Ordenar: intento ASC, luego fecha DESC, luego nombre
    return [...base].sort((a, b) => {
      if (a.intento !== b.intento) return a.intento - b.intento;
      if (a.fecha !== b.fecha) return b.fecha.localeCompare(a.fecha);
      return a.nombre_trabajador.localeCompare(b.nombre_trabajador);
    });
  }, [rows, filtros.search]);

  useEffect(() => { setPage(1); }, [filtros.search, filtros.capacitacion_id, filtros.desde, filtros.hasta, filtros.resultado]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  const stats = useMemo(() => {
    const total = filtered.length;
    const pres = filtered.filter(r => r.asistencia === 'presente').length;
    const aus = filtered.filter(r => r.asistencia === 'ausente').length;
    const apr = filtered.filter(r => r.resultado === 'aprobado').length;
    const rep = filtered.filter(r => r.resultado === 'reprobado').length;
    const pend = filtered.filter(r => !r.asistencia).length;
    return { total, pres, aus, apr, rep, pend };
  }, [filtered]);

  const exportCSV = () => {
    const header = ['Fecha', 'Capacitación', 'RUT', 'Nombre', 'Empresa', 'N° Intento', 'Asistencia', 'Resultado', 'Nota'];
    const rowsCsv = filtered.map(r => [
      r.fecha, r.capacitacion_nombre, r.rut_trabajador, r.nombre_trabajador,
      r.empresa_nombre, r.intento, r.asistencia || '', r.resultado || '',
      r.nota != null ? r.nota : ''
    ]);
    const csv = [header, ...rowsCsv].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_capacitaciones_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV descargado');
  };

  return (
    <div className="p-6 lg:p-8 space-y-5 bg-slate-50 min-h-screen" data-testid="reporte-capacitaciones">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-teal-700 mb-1">Reportes</p>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Detalle de Capacitaciones</h1>
          <p className="text-sm text-slate-500 mt-1">Listado completo de inscripciones con n° de intento y resultados.</p>
        </div>
        <button onClick={exportCSV}
          data-testid="btn-export-csv"
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700">
          <Download className="w-4 h-4" /> Exportar CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Total" value={stats.total} color="#0F766E" />
        <Stat label="Presentes" value={stats.pres} color="#059669" />
        <Stat label="Ausentes" value={stats.aus} color="#DC2626" />
        <Stat label="Aprobados" value={stats.apr} color="#10B981" />
        <Stat label="Reprobados" value={stats.rep} color="#B91C1C" />
        <Stat label="Sin evaluar" value={stats.pend} color="#64748B" />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="filtros-reporte">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <p className="text-[11px] uppercase tracking-wider font-bold text-slate-600">Filtros</p>
        </div>
        <div className="grid md:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 mb-1">Capacitación</label>
            <select value={filtros.capacitacion_id}
              data-testid="filtro-capacitacion"
              onChange={e => setFiltros(f => ({ ...f, capacitacion_id: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white">
              <option value="">Todas</option>
              {caps.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 mb-1">Desde</label>
            <input type="date" value={filtros.desde}
              onChange={e => setFiltros(f => ({ ...f, desde: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 mb-1">Hasta</label>
            <input type="date" value={filtros.hasta}
              onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 mb-1">Resultado</label>
            <select value={filtros.resultado}
              onChange={e => setFiltros(f => ({ ...f, resultado: e.target.value }))}
              data-testid="filtro-resultado"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white">
              <option value="">Todos</option>
              <option value="presente">Presentes</option>
              <option value="ausente">Ausentes</option>
              <option value="aprobado">Aprobados</option>
              <option value="reprobado">Reprobados</option>
              <option value="sin_evaluar">Sin evaluar</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 mb-1">Buscar</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input type="text" value={filtros.search}
                placeholder="RUT, nombre, empresa..."
                onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
                data-testid="filtro-search"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">Fecha</th>
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">Capacitación</th>
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">RUT</th>
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">Nombre</th>
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">Empresa</th>
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-center whitespace-nowrap">N° Intento</th>
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-center whitespace-nowrap">Asistencia</th>
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-center whitespace-nowrap">Resultado</th>
                <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-center whitespace-nowrap">Nota</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-10 text-center text-slate-400">Cargando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="py-10 text-center text-slate-400">Sin registros</td></tr>
              ) : paged.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`rep-row-${r.id}`}>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{formatFecha(r.fecha)}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap font-semibold text-slate-700">{r.capacitacion_nombre}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap font-mono text-slate-600">{r.rut_trabajador}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-slate-800">{r.nombre_trabajador}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-slate-600">{r.empresa_nombre || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-[11px] font-bold ${
                      r.intento === 1 ? 'bg-slate-100 text-slate-600' : r.intento === 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>#{r.intento}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Pill value={r.asistencia} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Pill value={r.resultado} />
                  </td>
                  <td className="px-3 py-2 text-center text-xs font-semibold">
                    {r.nota != null ? <span className={r.nota >= 85 ? 'text-emerald-700' : 'text-red-600'}>{r.nota}</span> : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 flex-wrap gap-2" data-testid="paginacion-reporte">
            <p className="text-[11px] text-slate-500">
              Mostrando <strong>{(currentPage - 1) * PAGE_SIZE + 1}</strong>–<strong>{Math.min(currentPage * PAGE_SIZE, filtered.length)}</strong> de <strong>{filtered.length}</strong> registros
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={currentPage === 1}
                data-testid="pag-first"
                className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                data-testid="pag-prev"
                className="px-2.5 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed">‹ Anterior</button>
              {buildPageWindow(currentPage, totalPages).map((p, idx) => (
                p === '…' ? (
                  <span key={`e${idx}`} className="px-1 text-slate-400 text-xs select-none">…</span>
                ) : (
                  <button key={p} onClick={() => setPage(p)}
                    data-testid={`pag-page-${p}`}
                    className={`min-w-[28px] px-2 py-1 text-xs rounded border transition-colors ${p === currentPage ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>{p}</button>
                )
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                data-testid="pag-next"
                className="px-2.5 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed">Siguiente ›</button>
              <button onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
                data-testid="pag-last"
                className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed">»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3">
      <div className="w-1 h-10 rounded-full" style={{ backgroundColor: color }} />
      <div>
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
        <p className="text-2xl font-black text-slate-900 leading-none mt-1">{value}</p>
      </div>
    </div>
  );
}

function Pill({ value }) {
  if (!value) return <span className="text-[11px] text-slate-300">—</span>;
  const label = { presente: 'Presente', ausente: 'Ausente', aprobado: 'Aprobado', reprobado: 'Reprobado' }[value] || value;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${RES_STYLES[value] || RES_STYLES.pendiente}`}>
      {label}
    </span>
  );
}

function formatFecha(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', year: 'numeric'
  }).format(new Date(iso + 'T12:00:00')).replace(/\//g, '-');
}

/** Ventana de páginas para paginación (max ~7 elementos): 1 … c-1 c c+1 … N */
function buildPageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  if (current <= 3) [2, 3, 4].forEach(p => pages.add(p));
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach(p => pages.add(p));
  const sorted = [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('…');
    out.push(sorted[i]);
  }
  return out;
}
